import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, MapPin, ArrowRight, Loader2, Search, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { IndicativeDisclaimer } from "@/components/IndicativeDisclaimer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export const SocietySetup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [prefeasibilityLoading, setPrefeasibilityLoading] = useState(false);
  const [existingSociety, setExistingSociety] = useState(null);
  const [prefeasibilityResult, setPrefeasibilityResult] = useState(null);
  const [createdSociety, setCreatedSociety] = useState(null);
  const [duplicate, setDuplicate] = useState(null); // {message, society, match_reason}
  const [claimingDup, setClaimingDup] = useState(false);
  const [step, setStep] = useState(1); // 1: Location, 2: Pre-feasibility, 3: Society Details
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    registration_number: "",
    total_flats: "",
    year_built: "",
    plot_area: "",
    carpet_area: "",
    builtup_area: "",
    fsi_available: "",
    fsi_consumed: "",
    location_lat: "",
    location_lng: "",
    location_address: "",
    zone: "",
    ward: "",
    building_age: ""
  });

  useEffect(() => {
    fetchExistingSociety();
    loadGoogleMapsScript();
  }, []);

  const loadGoogleMapsScript = () => {
    if (window.google) {
      initAutocomplete();
      return;
    }
    
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.onload = initAutocomplete;
    document.head.appendChild(script);
  };

  const initAutocomplete = () => {
    if (!inputRef.current || !window.google) return;
    
    autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: "in" },
      fields: ["formatted_address", "geometry", "name"],
      types: ["establishment", "geocode"]
    });
    
    autocompleteRef.current.addListener("place_changed", () => {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry) {
        setFormData(prev => ({
          ...prev,
          location_address: place.formatted_address || place.name,
          location_lat: place.geometry.location.lat().toFixed(6),
          location_lng: place.geometry.location.lng().toFixed(6),
          address: place.formatted_address || place.name
        }));
        toast.success("Location selected");
      }
    });
  };

  const fetchExistingSociety = async () => {
    try {
      const res = await fetch(`${API}/societies`, { credentials: "include" });
      const societies = await res.json();
      if (societies.length > 0) {
        const society = societies[0];
        setExistingSociety(society);
        setFormData({
          name: society.name || "",
          address: society.address || "",
          registration_number: society.registration_number || "",
          total_flats: society.total_flats?.toString() || "",
          year_built: society.year_built?.toString() || "",
          plot_area: society.plot_area?.toString() || "",
          carpet_area: society.carpet_area?.toString() || "",
          builtup_area: society.builtup_area?.toString() || "",
          fsi_available: society.fsi_available?.toString() || "",
          fsi_consumed: society.fsi_consumed?.toString() || "",
          location_lat: society.location_lat?.toString() || "",
          location_lng: society.location_lng?.toString() || "",
          location_address: society.location_address || "",
          zone: society.zone || "",
          ward: society.ward || "",
          building_age: society.year_built ? (new Date().getFullYear() - society.year_built).toString() : ""
        });
        
        if (society.prefeasibility_done) {
          setStep(3);
          setPrefeasibilityResult({ is_viable: society.prefeasibility_viable });
        } else if (society.location_lat) {
          setStep(2);
        }
      }
    } catch (error) {
      console.error("Failed to fetch society:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      toast.info("Detecting your location...");
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude.toFixed(6);
          const lng = position.coords.longitude.toFixed(6);
          
          setFormData(prev => ({
            ...prev,
            location_lat: lat,
            location_lng: lng
          }));
          
          // Reverse geocode to get address
          try {
            const response = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`
            );
            const data = await response.json();
            if (data.results && data.results[0]) {
              setFormData(prev => ({
                ...prev,
                location_address: data.results[0].formatted_address,
                address: data.results[0].formatted_address
              }));
            }
          } catch (e) {
            console.error("Reverse geocode failed:", e);
          }
          
          toast.success("Location detected");
        },
        () => {
          toast.error("Unable to get location. Please enter manually.");
        }
      );
    }
  };

  const runPrefeasibility = async () => {
    if (!formData.location_lat || !formData.location_lng) {
      toast.error("Please select a location first");
      return;
    }
    
    setPrefeasibilityLoading(true);
    toast.info("AI is analyzing location. This may take 30-60 seconds...", { duration: 5000 });
    
    try {
      const payload = {
        location_lat: parseFloat(formData.location_lat),
        location_lng: parseFloat(formData.location_lng),
        location_address: formData.location_address,
        plot_area: formData.plot_area ? parseFloat(formData.plot_area) : null,
        carpet_area: formData.carpet_area ? parseFloat(formData.carpet_area) : null,
        builtup_area: formData.builtup_area ? parseFloat(formData.builtup_area) : null,
        current_fsi: formData.fsi_consumed ? parseFloat(formData.fsi_consumed) : null,
        building_age: formData.building_age ? parseInt(formData.building_age) : null,
        total_flats: formData.total_flats ? parseInt(formData.total_flats) : null
      };
      
      const url = existingSociety 
        ? `${API}/societies/${existingSociety.society_id}/prefeasibility`
        : `${API}/prefeasibility`;
      
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include"
      });
      
      if (res.ok) {
        const result = await res.json();
        setPrefeasibilityResult(result);
        setFormData(prev => ({ ...prev, zone: result.zone }));
        
        if (result.is_viable) {
          toast.success("Location is VIABLE for redevelopment!");
          setStep(3);
        } else {
          toast.warning("Location may have challenges. Review the analysis.");
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || "Pre-feasibility analysis failed");
      }
    } catch (error) {
      toast.error("Pre-feasibility analysis failed");
    } finally {
      setPrefeasibilityLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        address: formData.address || formData.location_address,
        registration_number: formData.registration_number || null,
        total_flats: formData.total_flats ? parseInt(formData.total_flats) : 0,
        year_built: formData.year_built ? parseInt(formData.year_built) : null,
        plot_area: formData.plot_area ? parseFloat(formData.plot_area) : null,
        carpet_area: formData.carpet_area ? parseFloat(formData.carpet_area) : null,
        builtup_area: formData.builtup_area ? parseFloat(formData.builtup_area) : null,
        fsi_available: formData.fsi_available ? parseFloat(formData.fsi_available) : null,
        fsi_consumed: formData.fsi_consumed ? parseFloat(formData.fsi_consumed) : null,
        location_lat: formData.location_lat ? parseFloat(formData.location_lat) : null,
        location_lng: formData.location_lng ? parseFloat(formData.location_lng) : null,
        location_address: formData.location_address,
        zone: formData.zone,
        ward: formData.ward,
        prefeasibility_done: !!prefeasibilityResult,
        prefeasibility_viable: prefeasibilityResult?.is_viable || false
      };

      const url = existingSociety 
        ? `${API}/societies/${existingSociety.society_id}`
        : `${API}/societies`;
      
      const method = existingSociety ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include"
      });

      if (res.ok) {
        const result = await res.json();
        if (existingSociety) {
          toast.success("Society updated successfully");
          navigate("/dashboard");
        } else {
          toast.success("Society created successfully");
          setCreatedSociety(result);
        }
      } else {
        const error = await res.json();
        if (res.status === 409 && error.detail && typeof error.detail === "object") {
          setDuplicate(error.detail);
        } else {
          toast.error(typeof error.detail === "string" ? error.detail : "Failed to save society");
        }
      }
    } catch (error) {
      toast.error("Failed to save society");
    } finally {
      setLoading(false);
    }
  };

  const mapUrl = formData.location_lat && formData.location_lng
    ? `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${formData.location_lat},${formData.location_lng}&zoom=16`
    : `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=Mumbai,India&zoom=11`;

  const handleClaimDuplicate = async () => {
    setClaimingDup(true);
    try {
      const res = await fetch(`${API}/leads/expert-review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          society_id: duplicate?.society?.society_id,
          note: `User attempted to create a duplicate of "${duplicate?.society?.name}" and wants to claim the existing profile.`,
          context: "duplicate_claim"
        }),
        credentials: "include"
      });
      if (res.ok) {
        toast.success("Request sent — our team will connect with you to claim this society.");
        navigate("/dashboard");
      } else {
        toast.error("Could not send claim request. Please try again.");
      }
    } catch (e) {
      toast.error("Could not send claim request. Please try again.");
    } finally {
      setClaimingDup(false);
    }
  };

  if (duplicate) {
    const dup = duplicate.society || {};
    return (
      <DashboardLayout>
        <div className="max-w-xl mx-auto py-16">
          <Card data-testid="duplicate-society-card">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }} data-testid="duplicate-title">
                This society already exists
              </h2>
              <p className="text-slate-600 mb-6">
                {duplicate.message || "To keep one profile per society, please claim the existing one instead of creating a new profile."}
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-left mb-8">
                <p className="font-semibold text-slate-900" data-testid="duplicate-name">{dup.name}</p>
                {dup.address && <p className="text-sm text-slate-600 mt-1">{dup.address}</p>}
                <p className="text-xs text-slate-400 mt-2">
                  Matched by {duplicate.match_reason === "proximity" ? "location proximity" : "society name"}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={handleClaimDuplicate}
                  disabled={claimingDup}
                  className="bg-emerald-700 hover:bg-emerald-800"
                  data-testid="claim-existing-society-btn"
                >
                  {claimingDup ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Claim this Society
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setDuplicate(null)}
                  data-testid="duplicate-back-btn"
                >
                  Go Back
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (createdSociety) {
    return (
      <DashboardLayout>
        <div className="max-w-xl mx-auto py-16">
          <Card>
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }} data-testid="society-created-title">
                Society Created!
              </h2>
              <p className="text-slate-600 mb-8" data-testid="society-created-message">
                Your society has been created. You can check pre-feasibility for free based on the location and primary information provided by you and retrieved by our app.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => navigate("/prefeasibility")} className="bg-emerald-700 hover:bg-emerald-800" data-testid="check-prefeasibility-btn">
                  Check Pre-feasibility (Free)
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button variant="outline" onClick={() => navigate("/dashboard")} data-testid="go-dashboard-btn">
                  Go to Dashboard
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-6">
                To unlock Document Vault and advanced services, claim this society from your dashboard — our team will verify and connect with you soon.
              </p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto py-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[
            { num: 1, label: "Location" },
            { num: 2, label: "Pre-feasibility" },
            { num: 3, label: "Details" }
          ].map((s, idx) => (
            <div key={s.num} className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold ${
                step >= s.num ? "bg-emerald-700 text-white" : "bg-slate-200 text-slate-500"
              }`}>
                {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
              </div>
              <span className={`ml-2 text-sm ${step >= s.num ? "text-slate-900" : "text-slate-500"}`}>
                {s.label}
              </span>
              {idx < 2 && (
                <div className={`w-16 h-0.5 mx-4 ${step > s.num ? "bg-emerald-700" : "bg-slate-200"}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Location */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-emerald-700" />
                </div>
                <div>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Step 1: Select Location</CardTitle>
                  <CardDescription>Search or detect your society location</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Google Places Search */}
              <div>
                <Label>Search Society Location</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                  <Input
                    ref={inputRef}
                    placeholder="Search for your society address..."
                    className="pl-10"
                    data-testid="location-search-input"
                  />
                </div>
              </div>

              <div className="text-center text-slate-500">or</div>

              <Button 
                variant="outline" 
                className="w-full"
                onClick={getCurrentLocation}
                data-testid="detect-location-btn"
              >
                <MapPin className="w-4 h-4 mr-2" />
                Use Current Location (GPS)
              </Button>

              {/* Map Preview */}
              <div className="h-64 rounded-lg overflow-hidden border">
                <iframe
                  title="Location Map"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  src={mapUrl}
                />
              </div>

              {formData.location_address && (
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-sm text-emerald-700 font-medium">Selected Location:</p>
                  <p className="text-slate-700">{formData.location_address}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.location_lat}, {formData.location_lng}
                  </p>
                </div>
              )}

              {/* Basic info for prefeasibility */}
              <p className="text-sm text-slate-500">We use this to check FSI and DCPR/UDCPR eligibility for your plot.</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="total_flats">Total Flats (approx)</Label>
                  <Input
                    id="total_flats"
                    name="total_flats"
                    type="number"
                    value={formData.total_flats}
                    onChange={handleChange}
                    placeholder="e.g., 48"
                  />
                  <p className="text-xs text-slate-400 mt-1">A rough count is fine.</p>
                </div>
                <div>
                  <Label htmlFor="building_age">Building Age (years)</Label>
                  <Input
                    id="building_age"
                    name="building_age"
                    type="number"
                    value={formData.building_age}
                    onChange={handleChange}
                    placeholder="e.g., 30"
                  />
                  <p className="text-xs text-slate-400 mt-1">Approximate years since construction.</p>
                </div>
              </div>

              <Button 
                onClick={() => setStep(2)}
                disabled={!formData.location_lat || !formData.total_flats || !formData.building_age}
                className="w-full bg-emerald-700 hover:bg-emerald-800"
                data-testid="next-step-btn"
              >
                Continue to Pre-feasibility
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              {(!formData.location_lat || !formData.total_flats || !formData.building_age) && (
                <p className="text-xs text-amber-600 text-center">Set your location and enter total flats & building age to continue.</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 2: Pre-feasibility */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-emerald-700" />
                </div>
                <div>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Step 2: Pre-feasibility Analysis</CardTitle>
                  <CardDescription>AI analysis based on UDCPR & DCR regulations</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-700">Location: {formData.location_address}</p>
                <p className="text-sm text-slate-500">Zone: {formData.zone || 'Will be determined'}</p>
              </div>

              <IndicativeDisclaimer />

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="plot_area">Plot Area (sq.m)</Label>
                  <Input
                    id="plot_area"
                    name="plot_area"
                    type="number"
                    value={formData.plot_area}
                    onChange={handleChange}
                    placeholder="e.g., 2500"
                  />
                </div>
                <div>
                  <Label htmlFor="carpet_area">Total Carpet Area (sq.ft)</Label>
                  <Input
                    id="carpet_area"
                    name="carpet_area"
                    type="number"
                    value={formData.carpet_area}
                    onChange={handleChange}
                    placeholder="e.g., 25000"
                  />
                </div>
                <div>
                  <Label htmlFor="builtup_area">Built-up Area (sq.ft)</Label>
                  <Input
                    id="builtup_area"
                    name="builtup_area"
                    type="number"
                    value={formData.builtup_area}
                    onChange={handleChange}
                    placeholder="e.g., 35000"
                  />
                </div>
              </div>

              <Button 
                onClick={runPrefeasibility}
                disabled={prefeasibilityLoading}
                className="w-full bg-emerald-700 hover:bg-emerald-800"
                data-testid="run-prefeasibility-btn"
              >
                {prefeasibilityLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing with AI...
                  </>
                ) : (
                  <>Run Pre-feasibility Analysis</>
                )}
              </Button>

              {prefeasibilityResult && (
                <div className={`p-4 rounded-lg border ${
                  prefeasibilityResult.is_viable 
                    ? "bg-emerald-50 border-emerald-200" 
                    : "bg-amber-50 border-amber-200"
                }`}>
                  <div className="flex items-center gap-2 mb-3">
                    {prefeasibilityResult.is_viable ? (
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                    )}
                    <span className={`font-semibold ${
                      prefeasibilityResult.is_viable ? "text-emerald-700" : "text-amber-700"
                    }`}>
                      {prefeasibilityResult.is_viable ? "VIABLE for Redevelopment" : "Needs Review"}
                    </span>
                  </div>
                  {prefeasibilityResult.analysis && (
                    <div className="text-sm text-slate-700 max-h-64 overflow-y-auto whitespace-pre-wrap">
                      {prefeasibilityResult.analysis.substring(0, 1000)}...
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-4">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                  Back
                </Button>
                <Button 
                  onClick={() => setStep(3)}
                  disabled={!prefeasibilityResult}
                  className="flex-1 bg-emerald-700 hover:bg-emerald-800"
                  data-testid="continue-to-details-btn"
                >
                  Continue to Details
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Society Details */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-emerald-700" />
                </div>
                <div>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Step 3: Society Details</CardTitle>
                  <CardDescription>Complete your society registration</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid gap-4">
                  <div>
                    <Label htmlFor="name">Society Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="e.g., Shanti Nagar CHS Ltd"
                      required
                      data-testid="society-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address">Full Address</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address || formData.location_address}
                      onChange={handleChange}
                      placeholder="Auto-filled from location"
                    />
                  </div>
                  <div>
                    <Label htmlFor="registration_number">Registration Number</Label>
                    <Input
                      id="registration_number"
                      name="registration_number"
                      value={formData.registration_number}
                      onChange={handleChange}
                      placeholder="e.g., MUM/WS/HSG/TC/12345"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="total_flats">Total Flats</Label>
                    <Input
                      id="total_flats"
                      name="total_flats"
                      type="number"
                      value={formData.total_flats}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <Label htmlFor="year_built">Year Built</Label>
                    <Input
                      id="year_built"
                      name="year_built"
                      type="number"
                      value={formData.year_built}
                      onChange={handleChange}
                      placeholder="e.g., 1985"
                    />
                  </div>
                  <div>
                    <Label htmlFor="fsi_available">FSI Available</Label>
                    <Input
                      id="fsi_available"
                      name="fsi_available"
                      type="number"
                      step="0.1"
                      value={formData.fsi_available}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <Label htmlFor="fsi_consumed">FSI Consumed</Label>
                    <Input
                      id="fsi_consumed"
                      name="fsi_consumed"
                      type="number"
                      step="0.1"
                      value={formData.fsi_consumed}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button type="button" variant="outline" onClick={() => setStep(2)} className="flex-1">
                    Back
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 bg-emerald-700 hover:bg-emerald-800"
                    disabled={loading}
                    data-testid="save-society-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        {existingSociety ? "Update Society" : "Create Society"}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};
